import React, { useContext, useState } from 'react';
import { Box, Button } from 'rebass';
import styled from 'styled-components';

import Icon from '../Icon/Icon';
import { PageContext } from '../PageContext/PageContext';
import Typography from '../Typography/Typography';

import { theme } from '../../styles/theme';

const Select = styled(Button)`
  && {
    border: 1px solid currentColor;
    border-radius: 0;
    cursor: pointer;
    position: relative;
    text-align: left;

    & > svg {
      margin-left: 8px;
      stroke-width: 1.5px;

      & > path {
        stroke: currentColor;
      }
    }

    &[data-active='true'] > .options-box {
      display: block;
    }
  }
`;

const Option = styled(Button)`
  && {
    border: none;
    color: ${theme.colors.text};
    cursor: pointer;
    display: block;
    padding: ${theme.space[1]} ${theme.space[4]};
    text-align: left;
    width: 100%;

    &:hover {
      background: ${theme.colors.greyText};
    }

    & > * {
      text-transform: capitalize;
    }
  }
`;

/**
 * Markup Select component
 * @param {Object} props
 * @param {string} [props.emptyLabel]
 * @param {string} [props.name]
 * @param {Function} [props.onChange] - optional onChange when using this as a controlled component
 * @param {string} [props.prompt] - optional onChange when using this as a controlled component
 * @param {string} [props.value] - optional value when using this as a controlled component
 * @param {string[]} props.values
 */
export const SelectBox = ({
  className,
  emptyLabel,
  name,
  onChange,
  prompt,
  value,
  values,
}) => {
  const { data, dispatch } = useContext(PageContext);
  const [active, setActive] = useState(false);

  const handleClick = () => setActive(!active);
  const handleKeyDown = (e) => {
    if (e.keyCode == 13) {
      handleClick();
    }
  };

  const handleSelect = (val) => () => {
    name && dispatch(name, val);
    onChange && onChange(val);
  };

  const internalValue = value || data[name];

  return (
    <Select
      aria-label={internalValue || prompt}
      className={className}
      data-active={active}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      alignItems="center"
      color="inherit"
      display="flex"
      justifyContent="space-between"
      px="4"
      py="2"
      width="14rem"
    >
      <Typography
        inline
        variant="body-small"
        sx={{ textTransform: 'capitalize' }}
      >
        {internalValue === ''
          ? emptyLabel
          : internalValue || prompt || '\u00A0'}
      </Typography>
      <Icon icon="chevron" />
      <Box
        backgroundColor="background"
        className="options-box"
        display="none"
        width="14rem"
        sx={{
          borderColor: theme.colors.black,
          borderStyle: 'solid',
          borderWidth: 1,
          borderTopWidth: 0,
          left: '-1px', // offset from parent border
          position: 'absolute',
          top: '102%',
          zIndex: 5,
        }}
      >
        {emptyLabel ? (
          <Option onClick={handleSelect('')} tabIndex={0}>
            <Typography inline variant="body-small">
              {emptyLabel}
            </Typography>
          </Option>
        ) : null}
        {values.map((val) => (
          <Option key={val} onClick={handleSelect(val)} tabIndex={0}>
            <Typography inline variant="body-small">
              {val}
            </Typography>
          </Option>
        ))}
      </Box>
    </Select>
  );
};

export default SelectBox;
